/**
 * PlatformOrgDetail — one organization for a platform admin: plan, usage,
 * billing ids, members, venues, recent game nights, the audit log, and the
 * write actions (plan, trial, overrides, suspend). Each action has a confirm
 * step with a required reason.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Code,
  DataList,
  Flex,
  Grid,
  Heading,
  Link,
  Select,
  Spinner,
  Table,
  Text,
  TextField,
} from '@radix-ui/themes';
import {
  fetchPlatformOrg,
  formatDate,
  runPlatformAction,
  type PlatformAction,
  type PlatformOrgDetail as Detail,
} from '../../utils/platform';
import { FEATURE_LABELS, LIMIT_LABELS, type FeatureKey, type LimitKey } from '../../utils/billing';
import ActionDialog from './ActionDialog';
import { StatusBadge } from './PlatformOrgs';

type FeatureChoice = 'plan' | 'on' | 'off';
type LimitChoice = { mode: 'plan' | 'number' | 'unlimited'; value: string };

function errorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } } };
  return e.response?.data?.message || 'The change failed. Try again.';
}

function initFeatureChoices(d: Detail): Record<string, FeatureChoice> {
  return Object.fromEntries(d.featureKeys.map((k) => {
    const v = d.overrides.features[k];
    return [k, v === true ? 'on' : v === false ? 'off' : 'plan'];
  }));
}

function initLimitChoices(d: Detail): Record<string, LimitChoice> {
  return Object.fromEntries(d.limitKeys.map((k) => {
    if (!(k in d.overrides.limits)) return [k, { mode: 'plan', value: '' }];
    const v = d.overrides.limits[k];
    return [k, v === null || v === undefined ? { mode: 'unlimited', value: '' } : { mode: 'number', value: String(v) }];
  }));
}

function JsonCell({ value }: { value: unknown }) {
  if (value === null || value === undefined) return <Text color="gray">—</Text>;
  return <Code size="1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(value)}</Code>;
}

const PlatformOrgDetail: React.FC = () => {
  const { id } = useParams();
  const [d, setD] = useState<Detail | null>(null);
  const [loadError, setLoadError] = useState(false);

  // Form state for the action dialogs.
  const [plan, setPlan] = useState('company');
  const [expiresAt, setExpiresAt] = useState('');
  const [trialMode, setTrialMode] = useState<'extend' | 'reset'>('extend');
  const [trialDays, setTrialDays] = useState('14');
  const [featureChoices, setFeatureChoices] = useState<Record<string, FeatureChoice>>({});
  const [limitChoices, setLimitChoices] = useState<Record<string, LimitChoice>>({});

  const apply = useCallback((detail: Detail) => {
    setD(detail);
    setFeatureChoices(initFeatureChoices(detail));
    setLimitChoices(initLimitChoices(detail));
  }, []);

  useEffect(() => {
    if (!id) return;
    fetchPlatformOrg(id).then(apply).catch(() => setLoadError(true));
  }, [id, apply]);

  if (loadError) {
    return <Callout.Root color="red"><Callout.Text>Could not load this organization.</Callout.Text></Callout.Root>;
  }
  if (!d) {
    return <Flex align="center" gap="2"><Spinner /><Text color="gray">Loading…</Text></Flex>;
  }

  const { org } = d;
  const run = (action: PlatformAction) => async (reason: string) => {
    try {
      apply(await runPlatformAction(org.id, action, reason));
      return undefined;
    } catch (err) {
      return errorMessage(err);
    }
  };

  const overridesAction = (): PlatformAction => {
    const features: Partial<Record<FeatureKey, boolean>> = {};
    for (const [k, c] of Object.entries(featureChoices)) {
      if (c !== 'plan') features[k as FeatureKey] = c === 'on';
    }
    const limits: Partial<Record<LimitKey, number | null>> = {};
    for (const [k, c] of Object.entries(limitChoices)) {
      if (c.mode === 'unlimited') limits[k as LimitKey] = null;
      if (c.mode === 'number') limits[k as LimitKey] = Number(c.value);
    }
    return { kind: 'overrides', body: { features, limits } };
  };

  return (
    <Flex direction="column" gap="4">
      <Box>
        <Link asChild size="2"><RouterLink to="/platform">← All organizations</RouterLink></Link>
        <Flex justify="between" align="start" gap="3" wrap="wrap" mt="2">
          <Box>
            <Heading size="6">{org.name}</Heading>
            <Text size="2" color="gray">{org.slug} · id {org.id}{org.ownerEmail ? ` · ${org.ownerEmail}` : ''}</Text>
          </Box>
          <Flex gap="2" align="center" wrap="wrap">
            <Badge variant="solid">{org.effectivePlan}</Badge>
            <StatusBadge status={org.suspendedAt ? 'suspended' : org.effectiveStatus} />
            {org.hasOverrides && <Badge color="purple" variant="soft">overrides</Badge>}
          </Flex>
        </Flex>
      </Box>

      {!d.billingEnabled && (
        <Callout.Root color="gray">
          <Callout.Text>
            Billing is off. Plan and trial changes take effect when billing is on. Overrides and suspension apply now.
          </Callout.Text>
        </Callout.Root>
      )}
      {org.suspendedAt && (
        <Callout.Root color="red">
          <Callout.Text>
            Suspended on {formatDate(org.suspendedAt)}{d.suspendedReason ? `: ${d.suspendedReason}` : ''}. The org cannot create
            anything new. Running game nights still work.
          </Callout.Text>
        </Callout.Root>
      )}

      <Grid columns={{ initial: '1', md: '2' }} gap="4">
        <Card>
          <Heading size="3" mb="2">Plan and usage</Heading>
          <DataList.Root size="2">
            <DataList.Item><DataList.Label>Stored plan</DataList.Label><DataList.Value>{org.plan} ({org.planStatus ?? 'none'})</DataList.Value></DataList.Item>
            <DataList.Item><DataList.Label>Trial ends</DataList.Label><DataList.Value>{org.trialEndsAt ? `${formatDate(org.trialEndsAt)} (${org.trialDaysLeft}d)` : '—'}</DataList.Value></DataList.Item>
            <DataList.Item><DataList.Label>Comp ends</DataList.Label><DataList.Value>{org.planStatus === 'comped' ? (org.planExpiresAt ? formatDate(org.planExpiresAt) : 'never') : '—'}</DataList.Value></DataList.Item>
            <DataList.Item><DataList.Label>Venues</DataList.Label><DataList.Value>{org.usage.venues}{org.limits.venues !== null ? ` / ${org.limits.venues}` : ''}</DataList.Value></DataList.Item>
            <DataList.Item><DataList.Label>Game nights this month</DataList.Label><DataList.Value>{org.usage.gameNightsPerMonth}{org.limits.gameNightsPerMonth !== null ? ` / ${org.limits.gameNightsPerMonth}` : ''}</DataList.Value></DataList.Item>
            <DataList.Item>
              <DataList.Label>AI credits this month</DataList.Label>
              <DataList.Value>
                {org.usage.aiCreditsPerMonth === null ? 'n/a' : org.usage.aiCreditsPerMonth}
                {org.limits.aiCreditsPerMonth !== null ? ` / ${org.limits.aiCreditsPerMonth}` : ''}
              </DataList.Value>
            </DataList.Item>
            <DataList.Item><DataList.Label>Last game night</DataList.Label><DataList.Value>{formatDate(org.lastGameNightAt)}</DataList.Value></DataList.Item>
            <DataList.Item><DataList.Label>Last activity</DataList.Label><DataList.Value>{formatDate(org.lastActivityAt)}</DataList.Value></DataList.Item>
            <DataList.Item><DataList.Label>Created</DataList.Label><DataList.Value>{formatDate(org.createdAt)}</DataList.Value></DataList.Item>
          </DataList.Root>
        </Card>
        <Card>
          <Heading size="3" mb="2">Billing</Heading>
          <DataList.Root size="2">
            <DataList.Item>
              <DataList.Label>Stripe customer</DataList.Label>
              <DataList.Value>
                {d.billing.links.customer
                  ? <Link href={d.billing.links.customer} target="_blank" rel="noopener noreferrer">{d.billing.stripeCustomerId}</Link>
                  : '—'}
              </DataList.Value>
            </DataList.Item>
            <DataList.Item>
              <DataList.Label>Stripe subscription</DataList.Label>
              <DataList.Value>
                {d.billing.links.subscription
                  ? <Link href={d.billing.links.subscription} target="_blank" rel="noopener noreferrer">{d.billing.stripeSubscriptionId}</Link>
                  : '—'}
              </DataList.Value>
            </DataList.Item>
          </DataList.Root>
          <Heading size="3" mt="4" mb="2">Features now (with billing on)</Heading>
          <Flex gap="1" wrap="wrap">
            {d.featureKeys.map((k) => (
              <Badge key={k} color={d.features[k] ? 'green' : 'gray'} variant="soft">
                {FEATURE_LABELS[k] ?? k}{k in d.overrides.features ? ' *' : ''}
              </Badge>
            ))}
          </Flex>
          <Text as="p" size="1" color="gray" mt="1">* set by an override</Text>
        </Card>
      </Grid>

      <Card>
        <Heading size="3" mb="3">Actions</Heading>
        <Flex gap="2" wrap="wrap">
          <ActionDialog
            trigger={<Button variant="soft">Change or comp plan</Button>}
            title="Change or comp the plan"
            description="A paid plan set here is a comp: Stripe does not charge for it. Free removes a comp."
            confirmLabel="Save plan"
            onConfirm={run({ kind: 'plan', body: { plan, expiresAt: plan === 'free' || !expiresAt ? null : new Date(expiresAt).toISOString() } })}
          >
            <label>
              <Text as="div" size="2" weight="medium" mb="1">Plan</Text>
              <Select.Root value={plan} onValueChange={setPlan}>
                <Select.Trigger />
                <Select.Content>
                  {d.plans.map((p) => <Select.Item key={p.key} value={p.key}>{p.name}</Select.Item>)}
                </Select.Content>
              </Select.Root>
            </label>
            {plan !== 'free' && (
              <label>
                <Text as="div" size="2" weight="medium" mb="1">Comp ends (optional, empty means never)</Text>
                <TextField.Root type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
              </label>
            )}
          </ActionDialog>

          <ActionDialog
            trigger={<Button variant="soft">Extend or reset trial</Button>}
            title="Change the trial"
            description="Extend adds days to the current trial end. Reset starts a new trial today."
            confirmLabel="Save trial"
            onConfirm={run({ kind: 'trial', body: { mode: trialMode, days: Number(trialDays) } })}
          >
            <Flex gap="3">
              <label>
                <Text as="div" size="2" weight="medium" mb="1">Mode</Text>
                <Select.Root value={trialMode} onValueChange={(v) => setTrialMode(v as 'extend' | 'reset')}>
                  <Select.Trigger />
                  <Select.Content>
                    <Select.Item value="extend">Extend</Select.Item>
                    <Select.Item value="reset">Reset</Select.Item>
                  </Select.Content>
                </Select.Root>
              </label>
              <label>
                <Text as="div" size="2" weight="medium" mb="1">Days</Text>
                <TextField.Root type="number" min="1" max="365" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} />
              </label>
            </Flex>
          </ActionDialog>

          <ActionDialog
            trigger={<Button variant="soft">Feature and limit overrides</Button>}
            title="Overrides"
            description="An override wins over the plan. Stripe updates never change it."
            confirmLabel="Save overrides"
            onConfirm={(reason) => run(overridesAction())(reason)}
          >
            <Grid columns="2" gap="2">
              {d.featureKeys.map((k) => (
                <label key={k}>
                  <Text as="div" size="1" mb="1">{FEATURE_LABELS[k] ?? k}</Text>
                  <Select.Root size="1" value={featureChoices[k] ?? 'plan'} onValueChange={(v) => setFeatureChoices((c) => ({ ...c, [k]: v as FeatureChoice }))}>
                    <Select.Trigger />
                    <Select.Content>
                      <Select.Item value="plan">From plan</Select.Item>
                      <Select.Item value="on">Force on</Select.Item>
                      <Select.Item value="off">Force off</Select.Item>
                    </Select.Content>
                  </Select.Root>
                </label>
              ))}
            </Grid>
            {d.limitKeys.map((k) => {
              const c = limitChoices[k] ?? { mode: 'plan', value: '' };
              return (
                <Flex key={k} gap="2" align="end">
                  <label>
                    <Text as="div" size="1" mb="1">{LIMIT_LABELS[k] ?? k}</Text>
                    <Select.Root size="1" value={c.mode} onValueChange={(v) => setLimitChoices((m) => ({ ...m, [k]: { ...c, mode: v as LimitChoice['mode'] } }))}>
                      <Select.Trigger />
                      <Select.Content>
                        <Select.Item value="plan">From plan</Select.Item>
                        <Select.Item value="number">Set a number</Select.Item>
                        <Select.Item value="unlimited">Unlimited</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  </label>
                  {c.mode === 'number' && (
                    <TextField.Root size="1" type="number" min="0" value={c.value} aria-label={`${LIMIT_LABELS[k] ?? k} limit`}
                      onChange={(e) => setLimitChoices((m) => ({ ...m, [k]: { ...c, value: e.target.value } }))} />
                  )}
                </Flex>
              );
            })}
          </ActionDialog>

          {org.suspendedAt ? (
            <ActionDialog
              trigger={<Button variant="soft" color="green">Unsuspend</Button>}
              title="Unsuspend this organization"
              description="The org can create venues and game nights again, within its plan."
              confirmLabel="Unsuspend"
              onConfirm={run({ kind: 'unsuspend', body: {} })}
            />
          ) : (
            <ActionDialog
              trigger={<Button variant="soft" color="red">Suspend</Button>}
              title="Suspend this organization"
              description="The org cannot create anything new. A game night that is running still works to the end. No data is deleted."
              confirmLabel="Suspend"
              color="red"
              onConfirm={run({ kind: 'suspend', body: {} })}
            />
          )}
        </Flex>
      </Card>

      <Grid columns={{ initial: '1', md: '2' }} gap="4">
        <Card>
          <Heading size="3" mb="2">Members ({d.members.length})</Heading>
          <Table.Root size="1">
            <Table.Body>
              {d.members.map((m) => (
                <Table.Row key={m.id}>
                  <Table.Cell>{m.email}</Table.Cell>
                  <Table.Cell><Badge variant="soft">{m.role}</Badge></Table.Cell>
                  <Table.Cell>{formatDate(m.lastSeenAt ?? m.lastLoginAt)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Card>
        <Card>
          <Heading size="3" mb="2">Venues ({d.venues.length})</Heading>
          <Flex direction="column" gap="1">
            {d.venues.map((v) => <Text key={v.id} size="2">{v.name}</Text>)}
            {d.venues.length === 0 && <Text size="2" color="gray">No venues</Text>}
          </Flex>
        </Card>
      </Grid>

      <Card>
        <Heading size="3" mb="2">Recent game nights</Heading>
        <Table.Root size="1">
          <Table.Body>
            {d.recentNights.map((n) => (
              <Table.Row key={n.id}>
                <Table.Cell>{formatDate(n.date)}</Table.Cell>
                <Table.Cell>{n.name || n.quiz?.name || 'Game night'}</Table.Cell>
                <Table.Cell>{n.venue?.name ?? '—'}</Table.Cell>
                <Table.Cell><Badge variant="soft">{n.status}</Badge></Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
        {d.recentNights.length === 0 && <Text size="2" color="gray">No game nights yet</Text>}
      </Card>

      <Card>
        <Heading size="3" mb="2">Audit log</Heading>
        <Box style={{ overflowX: 'auto' }}>
          <Table.Root size="1">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>When</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Who</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Action</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Reason</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Before</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>After</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {d.audit.map((a) => (
                <Table.Row key={a.id}>
                  <Table.Cell>{new Date(a.createdAt).toLocaleString()}</Table.Cell>
                  <Table.Cell>{a.actorEmail}</Table.Cell>
                  <Table.Cell><Code size="1">{a.action}</Code></Table.Cell>
                  <Table.Cell>{a.reason}</Table.Cell>
                  <Table.Cell><JsonCell value={a.before} /></Table.Cell>
                  <Table.Cell><JsonCell value={a.after} /></Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
        {d.audit.length === 0 && <Text size="2" color="gray">No platform-admin changes yet</Text>}
      </Card>
    </Flex>
  );
};

export default PlatformOrgDetail;
